import React, { useState, useEffect, useCallback } from "react";
import { Box } from "@mui/material";
import { C, fmtINR } from "../constants";
import { IPD_COLORS, ipdSemanticSx, ipdCardSx } from "../../ipd/utils/ipdColors";
import IpdTextField from "../../ipd/utils/IpdTextField";
import IpdFormField from "../../ipd/utils/IpdFormField";
import IpdButton from "../../ipd/utils/IpdButton";
import SvgIcon from "../SvgIcon";
import { useBillingPermissions } from "../useBillingPermissions";
import {
  BILLING_PERMS,
  PAYMENT_MODE_TO_API,
  formatPaymentModeLabel,
  requestReceiptCancel,
  requestPaymentRefund,
  createRefundInstruction,
  transferReceiptToDeposit,
  getPaymentWorkflowStatus,
  listApprovalRequests,
  toBillingError,
} from "../billing-api";

// ─────────────────────────────────────────────────────────────────────────────
// Payments / Receipts table with cancel → refund / transfer-to-deposit actions
// (spec Parts C & D). Approve + Execute steps live in the Approvals inbox (Phase 3);
// this panel drives the Cashier-side request + post-cancel instruction steps.
// ─────────────────────────────────────────────────────────────────────────────

// Robust field accessors — the payments endpoint isn't normalized upstream.
const pId = (p) => p.payment_id || p.id;
const pNumber = (p) => p.payment_number || p.receipt_number || p.number || "—";
const pStatus = (p) => String(p.payment_status || p.status || "").toUpperCase();
const pAmount = (p) => Number(p.amount ?? p.paid_amount ?? p.net_amount ?? 0);
const pMode = (p) => p.payment_mode || p.mode || "—";

const cancelRecord = (p) => p.cancellation || p.receipt_cancellation || {};
const refundRecord = (p) => p.refund || p.payment_refund || {};

// Per-payment workflow-status → row CTA vocabulary.
//   • receipt_cancel.next_action === "REFUND_OR_TRANSFER" is the post-cancel
//     decision point; receipt_cancel.post_cancel_action then says which track
//     (REFUND vs TRANSFER_TO_DEPOSIT) to offer.
//   • Once a refund is in flight, payment_refund.next_action drives the
//     create-instruction step.
// Each CTA is still gated on the cashier's permission.
const CANCEL_DECISION_ACTION = "REFUND_OR_TRANSFER";
const POST_CANCEL_REFUND = "REFUND";
const POST_CANCEL_TRANSFER = "TRANSFER_TO_DEPOSIT";
const REFUND_INSTRUCTION_ACTIONS = [
  "CREATE_REFUND_INSTRUCTION", "CREATE_INSTRUCTION", "REFUND_INSTRUCTION",
  "PAYMENT_REFUND_EXECUTE", "REFUND_EXECUTE",
];
const up = (v) => String(v || "").toUpperCase();

const TRANSFER_DONE = ["TRANSFERRED", "TRANSFERRED_TO_DEPOSIT", "COMPLETED", "DONE"];
const TRANSFER_PENDING = ["REQUESTED", "PENDING", "APPROVED", "IN_PROGRESS"];
const TRANSFER_FAILED = ["REJECTED", "FAILED", "CANCELLED"];

/** Badge tone for a deposit transfer — the backend ships a label but no tone. */
function transferTone(transfer) {
  const explicit = transfer.ui_status_tone || transfer.ui_badge_tone;
  if (explicit) return explicit;
  const s = up(transfer.transfer_status);
  if (TRANSFER_DONE.includes(s)) return "info";
  if (TRANSFER_PENDING.includes(s)) return "warning";
  if (TRANSFER_FAILED.includes(s)) return "danger";
  return "muted";
}

/** Derive a UI state for a payment row. */
function derivePaymentState(p) {
  const status = pStatus(p);
  const cancel = cancelRecord(p);
  const cancelStatus = String(
    p.cancel_request_status || cancel.cancellation_status || cancel.status || ""
  ).toUpperCase();
  const refund = refundRecord(p);
  const refundStatus = String(
    p.refund_status || refund.refund_status || refund.status || ""
  ).toUpperCase();

  const isCancelled =
    status === "CANCELLED" || cancelStatus === "CANCELLED";
  const cancelPending =
    !isCancelled &&
    ["REQUESTED", "PENDING", "APPROVED"].includes(cancelStatus);
  const postCancelAction = String(
    p.post_cancel_action || cancel.post_cancel_action || cancel.after_cancel_action || ""
  ).toUpperCase();
  // D1 done — the receipt amount has been moved into the patient deposit wallet.
  const transferStatus = String(
    p.deposit_transfer?.transfer_status || p.transfer_status || cancel.deposit_transfer_status || ""
  ).toUpperCase();

  return {
    status,
    isCancelled,
    cancelPending,
    postCancelAction,
    transferDone: TRANSFER_DONE.includes(transferStatus),
    refundId: p.refund_id || refund.refund_id || refund.id || null,
    refundStatus,
    refundApproved: ["APPROVED", "INSTRUCTION_CREATED", "COMPLETED"].includes(refundStatus),
    refundInstructionDone:
      ["INSTRUCTION_CREATED", "COMPLETED"].includes(refundStatus),
    cancelReason: p.cancellation_reason || cancel.reason || "",
  };
}

// ── Shared modal shell ────────────────────────────────────────────────────────
function ModalShell({ title, subtitle, onClose, children, footer, width = 440 }) {
  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(13,35,63,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
      onClick={onClose}
    >
      {/* boxShadow rgba(13,35,63,0.28) has no IPD_COLORS.shadow equivalent — left as-is, matches CashCounterUI.js precedent */}
      <Box
        sx={{ ...ipdCardSx, borderRadius: "16px", boxShadow: "0 24px 64px rgba(13,35,63,0.28)", p: "28px", width: `${width}px`, maxWidth: "92vw" }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ margin: "0 0 6px", fontSize: "17px", fontWeight: "800", color: C.text }}>{title}</h3>
        {subtitle && <p style={{ margin: "0 0 18px", fontSize: "12px", color: C.textSub }}>{subtitle}</p>}
        <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>{children}</div>
        <div style={{ display: "flex", gap: "10px", marginTop: "22px" }}>{footer}</div>
      </Box>
    </div>
  );
}

// ── Request Cancel modal (C1) ─────────────────────────────────────────────────
const POST_CANCEL_OPTIONS = [
  { key: "REFUND", label: "Refund after cancel", hint: "Pay the money back to the patient" },
  { key: "TRANSFER_TO_DEPOSIT", label: "Transfer to deposit", hint: "Move it to the patient's deposit wallet" },
];

function RequestCancelModal({ payment, onClose, onSubmit, busy }) {
  const full = pAmount(payment);
  const [reason, setReason] = useState("");
  const [action, setAction] = useState("REFUND");
  const [error, setError] = useState("");

  const submit = () => {
    if (!reason.trim()) { setError("Reason is required."); return; }
    onSubmit({ reason: reason.trim(), requested_amount: full.toFixed(2), post_cancel_action: action });
  };

  return (
    <ModalShell
      title="Request Receipt Cancellation"
      subtitle={`Receipt ${pNumber(payment)} · ${fmtINR(full)} · ${pMode(payment)}`}
      onClose={onClose}
      footer={<>
        <IpdButton variant="outlined" onClick={onClose} disabled={busy} sx={{ flex: 1 }}>Cancel</IpdButton>
        <IpdButton onClick={submit} disabled={busy} sx={{ flex: 2 }}>{busy ? "Submitting…" : "Submit Request"}</IpdButton>
      </>}
    >
      <IpdFormField label="Reason" required>
        <IpdTextField
          fullWidth
          value={reason}
          onChange={(e) => { setReason(e.target.value); setError(""); }}
          multiline
          rows={3}
          placeholder="Why is this receipt being cancelled?"
        />
      </IpdFormField>
      <IpdFormField label="Amount (₹)">
        <IpdTextField
          fullWidth
          value={full.toFixed(2)}
          InputProps={{ readOnly: true }}
          sx={{ "& .MuiOutlinedInput-root": { bgcolor: IPD_COLORS.pageBg }, "& .MuiOutlinedInput-input": { fontWeight: 700 } }}
        />
      </IpdFormField>
      <IpdFormField label="After cancel">
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {POST_CANCEL_OPTIONS.map((o) => (
            <button key={o.key} type="button" onClick={() => setAction(o.key)}
              style={{ display: "flex", alignItems: "center", gap: "10px", padding: "10px 12px", borderRadius: "9px", border: `1.5px solid ${action === o.key ? C.primary : C.border}`, background: action === o.key ? C.accent : IPD_COLORS.cardBg, cursor: "pointer", textAlign: "left" }}>
              <span style={{ width: "16px", height: "16px", borderRadius: "50%", border: `2px solid ${action === o.key ? C.primary : C.border}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                {action === o.key && <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: C.primary }} />}
              </span>
              <span>
                <span style={{ display: "block", fontSize: "13px", fontWeight: "700", color: C.text }}>{o.label}</span>
                <span style={{ display: "block", fontSize: "11px", color: C.textSub }}>{o.hint}</span>
              </span>
            </button>
          ))}
        </div>
      </IpdFormField>
      {error && <div style={{ fontSize: "12px", color: C.danger, fontWeight: "600" }}>{error}</div>}
    </ModalShell>
  );
}

// ── Refund request modal (C4) ─────────────────────────────────────────────────
// refund_mode is required by the backend but hidden here — sent as UPI by default.
function RefundRequestModal({ payment, onClose, onSubmit, busy }) {
  const full = pAmount(payment);
  const [amount, setAmount] = useState(full.toFixed(2));
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");

  const submit = () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) { setError("Enter a valid amount."); return; }
    if (amt > full + 0.01) { setError(`Refund cannot exceed ${fmtINR(full)}.`); return; }
    if (!reason.trim()) { setError("Reason is required."); return; }
    onSubmit({ refund_amount: amt.toFixed(2), refund_mode: "UPI", reason: reason.trim() });
  };

  return (
    <ModalShell
      title="Request Refund"
      subtitle={`Cancelled receipt ${pNumber(payment)} · ${fmtINR(full)}`}
      onClose={onClose}
      footer={<>
        <IpdButton variant="outlined" onClick={onClose} disabled={busy} sx={{ flex: 1 }}>Cancel</IpdButton>
        <IpdButton onClick={submit} disabled={busy} sx={{ flex: 2 }}>{busy ? "Submitting…" : "Request Refund"}</IpdButton>
      </>}
    >
      <IpdFormField label="Refund amount (₹)">
        <IpdTextField
          fullWidth
          type="number"
          inputProps={{ min: 0, step: 0.01 }}
          value={amount}
          onChange={(e) => { setAmount(e.target.value); setError(""); }}
          sx={{ "& .MuiOutlinedInput-input": { fontWeight: 700 } }}
        />
      </IpdFormField>
      <IpdFormField label="Reason" required>
        <IpdTextField
          fullWidth
          value={reason}
          onChange={(e) => { setReason(e.target.value); setError(""); }}
          placeholder="Why is this being refunded?"
        />
      </IpdFormField>
      {error && <div style={{ fontSize: "12px", color: C.danger, fontWeight: "600" }}>{error}</div>}
    </ModalShell>
  );
}

// ── Refund instruction modal (C6) ─────────────────────────────────────────────
// The refund mode was chosen at request time (C4), so it's read-only here. It
// defaults to UPI when the backend doesn't expose the chosen mode. Payload sends
// refund_mode + transaction_reference + remarks.
function RefundInstructionModal({ payment, presetMode, onClose, onSubmit, busy }) {
  const mode = presetMode || "UPI"; // default UPI until backend exposes the chosen mode
  const [reference, setReference] = useState("");
  const [remarks, setRemarks] = useState("");
  const [error, setError] = useState("");

  const submit = () => {
    if (!reference.trim()) { setError("Transaction reference is required."); return; }
    onSubmit({
      refund_mode: PAYMENT_MODE_TO_API[mode] || mode,
      transaction_reference: reference.trim(),
      remarks: remarks.trim(),
    });
  };

  return (
    <ModalShell
      title="Refund Instruction"
      subtitle={`Record the refund reference for ${pNumber(payment)}`}
      onClose={onClose}
      footer={<>
        <IpdButton variant="outlined" onClick={onClose} disabled={busy} sx={{ flex: 1 }}>Cancel</IpdButton>
        <IpdButton onClick={submit} disabled={busy} sx={{ flex: 2 }}>{busy ? "Saving…" : "Save Instruction"}</IpdButton>
      </>}
    >
      <IpdFormField
        label="Refund mode"
        helperText={presetMode ? "Chosen when the refund was requested." : "Defaulted to UPI."}
      >
        <IpdTextField
          fullWidth
          value={formatPaymentModeLabel(mode)}
          disabled
          InputProps={{ readOnly: true }}
          sx={{ "& .MuiOutlinedInput-root": { bgcolor: IPD_COLORS.pageBg }, "& .MuiOutlinedInput-input": { fontWeight: 700 } }}
        />
      </IpdFormField>
      <IpdFormField label="Transaction reference" required>
        <IpdTextField
          fullWidth
          value={reference}
          onChange={(e) => { setReference(e.target.value); setError(""); }}
          placeholder="UTR / cheque no. / txn id"
        />
      </IpdFormField>
      <IpdFormField label="Remarks">
        <IpdTextField fullWidth value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Optional note" />
      </IpdFormField>
      {error && <div style={{ fontSize: "12px", color: C.danger, fontWeight: "600" }}>{error}</div>}
    </ModalShell>
  );
}

// ── Transfer to deposit modal (D1) ────────────────────────────────────────────
function TransferToDepositModal({ payment, onClose, onSubmit, busy }) {
  const full = pAmount(payment);
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");

  const submit = () => {
    if (!reason.trim()) { setError("Reason is required."); return; }
    onSubmit({ transfer_amount: full.toFixed(2), reason: reason.trim() });
  };

  return (
    <ModalShell
      title="Transfer to Deposit"
      subtitle={`Move ${fmtINR(full)} from cancelled ${pNumber(payment)} to the patient deposit wallet`}
      onClose={onClose}
      footer={<>
        <IpdButton variant="outlined" onClick={onClose} disabled={busy} sx={{ flex: 1 }}>Cancel</IpdButton>
        <IpdButton onClick={submit} disabled={busy} sx={{ flex: 2 }}>{busy ? "Transferring…" : "Transfer"}</IpdButton>
      </>}
    >
      <IpdFormField label="Amount (₹)">
        <IpdTextField
          fullWidth
          value={full.toFixed(2)}
          InputProps={{ readOnly: true }}
          sx={{ "& .MuiOutlinedInput-root": { bgcolor: IPD_COLORS.pageBg }, "& .MuiOutlinedInput-input": { fontWeight: 700 } }}
        />
      </IpdFormField>
      <IpdFormField label="Reason" required>
        <IpdTextField
          fullWidth
          value={reason}
          onChange={(e) => { setReason(e.target.value); setError(""); }}
          placeholder="Why transfer to deposit?"
        />
      </IpdFormField>
      {error && <div style={{ fontSize: "12px", color: C.danger, fontWeight: "600" }}>{error}</div>}
    </ModalShell>
  );
}

// ── Status badge ──────────────────────────────────────────────────────────────
// Translates ipdSemanticSx's MUI-sx shape ({bgcolor, color}) into this file's
// local inline-style shape ({bg, fg}). This Badge's JSX doesn't render a
// border, so ipdSemanticSx's border is intentionally dropped here.
function semanticTone(variant) {
  const sx = ipdSemanticSx(variant);
  return { bg: sx.bgcolor, fg: sx.color };
}

function Badge({ text, tone }) {
  const tones = {
    success: semanticTone("success"),
    danger: semanticTone("error"),
    warning: semanticTone("warning"),
    info: semanticTone("info"), // nearest semantic tone — original #e6f0fb/#1a4a7a were a slightly different blue, no exact match
    muted: { bg: IPD_COLORS.pageBg, fg: C.textSub },
  };
  const t = tones[tone] || tones.muted;
  return (
    <span style={{ display: "inline-block", padding: "3px 9px", borderRadius: "100px", background: t.bg, color: t.fg, fontSize: "10px", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.04em" }}>{text}</span>
  );
}

// ── Main panel ────────────────────────────────────────────────────────────────
export default function PaymentsReceiptsPanel({ admissionId, payments = [], onRefresh, notify }) {
  const { can } = useBillingPermissions();
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState(null); // { type, payment }
  const [wf, setWf] = useState({}); // payment_id -> workflow-status entry (Part B2 badges)
  const [appr, setAppr] = useState({}); // payment_id -> { cancelAction, refundStatus, refundId }

  // Rank cancel-request statuses so the executed/most-advanced one wins per payment.
  const cancelRank = (s) => ({ EXECUTED: 3, APPROVED: 2, PENDING: 1, REQUESTED: 1 }[s] || 0);

  // Load backend-driven workflow badges + per-payment disposition (post_cancel_action)
  // and refund state from the approval-requests feed.
  const loadWorkflow = useCallback(async () => {
    if (!admissionId) { setWf({}); setAppr({}); return; }
    try {
      const [wfEntries, apprItems] = await Promise.all([
        // Per-payment workflow status — one call per receipt (spec Part B2).
        Promise.all(
          payments.map((p) => {
            const pid = pId(p);
            return pid
              ? getPaymentWorkflowStatus(admissionId, pid)
                  .then((w) => ({ pid, w }))
                  .catch(() => null)
              : null;
          })
        ),
        listApprovalRequests({ admissionId, queue: "all" }).catch(() => []),
      ]);
      const wfMap = {};
      wfEntries.forEach((e) => { if (e && e.w) wfMap[e.pid] = e.w; });
      const apprMap = {};
      apprItems.forEach((it) => {
        const pid = it.payment_id;
        if (!pid) return;
        const ctx = String(it.approval_context || "").toUpperCase();
        const status = String(it.request_status || "").toUpperCase();
        const entry = apprMap[pid] || {};
        if (ctx === "RECEIPT_CANCEL") {
          if (!entry.cancelStatus || cancelRank(status) >= cancelRank(entry.cancelStatus)) {
            entry.cancelAction = String(it.post_cancel_action || "").toUpperCase();
            entry.cancelStatus = status;
          }
        } else if (ctx === "PAYMENT_REFUND" && status !== "REJECTED") {
          entry.refundStatus = status;
          // create-instruction (C6) is keyed by refund_id (= workflow_detail_id).
          entry.refundId = it.refund_id || it.workflow_detail_id || it.approval_request_id;
          entry.refundMode = it.refund_mode || null; // shown read-only at instruction time
        }
        apprMap[pid] = entry;
      });
      setWf(wfMap);
      setAppr(apprMap);
    } catch {
      setWf({}); setAppr({}); // fall back to client-derived state
    }
  }, [admissionId, payments]);

  useEffect(() => { loadWorkflow(); }, [loadWorkflow, payments]);

  const say = (msg, isError) => notify && notify(msg, isError);

  const run = async (fn, successMsg) => {
    setBusy(true);
    try {
      await fn();
      if (successMsg) say(successMsg, false);
      setModal(null);
      if (onRefresh) await onRefresh();
      await loadWorkflow();
    } catch (err) {
      say(toBillingError(err), true);
    } finally {
      setBusy(false);
    }
  };

  const doRequestCancel = (payload) =>
    // payload carries post_cancel_action (REFUND | TRANSFER_TO_DEPOSIT) chosen in the modal.
    run(() => requestReceiptCancel(admissionId, pId(modal.payment), payload), "Cancellation request submitted for approval.");
  const doRefundRequest = (payload) =>
    run(() => requestPaymentRefund(admissionId, pId(modal.payment), payload), "Refund request submitted for approval.");
  const doRefundInstruction = (payload) =>
    run(() => createRefundInstruction(admissionId, modal.refundId, payload), "Refund instruction recorded.");
  const doTransfer = (payload) =>
    run(() => transferReceiptToDeposit(admissionId, pId(modal.payment), payload), "Added to patient deposit wallet.");

  if (!payments.length) {
    return (
      <div style={{ padding: "18px 20px", color: C.textSub, fontSize: "13px" }}>
        No payments or receipts yet. Collect a payment to create a receipt.
      </div>
    );
  }

  return (
    <div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
          <thead>
            <tr style={{ textAlign: "left", color: C.textSub, fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              <th style={{ padding: "10px 12px" }}>Receipt</th>
              <th style={{ padding: "10px 12px" }}>Mode</th>
              <th style={{ padding: "10px 12px", textAlign: "right" }}>Amount</th>
              <th style={{ padding: "10px 12px 10px 36px" }}>Status</th>
              <th style={{ padding: "10px 12px", textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {payments.map((p) => {
              const st = derivePaymentState(p);
              // Backend workflow badge (Part B2) takes precedence over client-derived state.
              const wfEntry = wf[pId(p)] || null;
              const wfCancel = wfEntry?.receipt_cancel || {};
              const wfTransfer = wfEntry?.deposit_transfer || null;
              const wfRefund = wfTransfer || wfEntry?.payment_refund || null;
              const wfCancelled = up(wfCancel.cancellation_status) === "CANCELLED" || up(wfCancel.request_status) === "EXECUTED";
              const wfPending = !wfCancelled && ["REQUESTED", "PENDING", "APPROVED"].includes(up(wfCancel.request_status));
              const cancelled = st.isCancelled || wfCancelled || pStatus(p) === "CANCELLED";
              const cancelPending = !cancelled && (st.cancelPending || wfPending);
              // Spec Part D — "Transfer to deposit" is only shown while the transfer is
              // pending (condition 3). Once D1 completes, the row reads "Transferred to
              // deposit" and the button is gone. Backend drives this via the workflow
              // badge; fall back to derived signals when it isn't provided.
              const transferDone =
                st.transferDone ||
                /transferred/i.test(String(wfEntry?.ui_badge_label || "")) ||
                TRANSFER_DONE.includes(up(wfTransfer?.transfer_status || wfEntry?.transfer_status));
              // A receipt cancelled into the deposit wallet has two legs, and the
              // receipt-level `ui_badge_label` only ever describes the cancel one
              // ("Receipt cancelled"). When the transfer leg exists it is the later,
              // more meaningful state, so its own label wins.
              const badge = wfTransfer?.ui_status_label
                ? { text: wfTransfer.ui_status_label, tone: transferTone(wfTransfer) }
                : wfEntry?.ui_badge_label
                ? { text: wfEntry.ui_badge_label, tone: wfEntry.ui_badge_tone || "muted" }
                : transferDone ? { text: "Transferred to deposit", tone: "info" }
                : cancelled ? { text: "Cancelled", tone: "danger" }
                : cancelPending ? { text: "Cancel pending", tone: "warning" }
                : { text: st.status || "Success", tone: "success" };
              const rowStyle = { borderTop: `1px solid ${C.border}`, opacity: cancelled ? 0.7 : 1 };
              const numStyle = { padding: "12px", fontWeight: "700", color: C.text, textDecoration: cancelled ? "line-through" : "none" };

              // Request Cancel is a direct row button (not tucked in a ⋮ menu).
              const showRequestCancel =
                !cancelled && !cancelPending && can(BILLING_PERMS.RECEIPT_CANCEL_REQUEST);

              // Post-cancel CTA — driven entirely by the per-payment workflow-status
              // payload. `receipt_cancel.next_action === "REFUND_OR_TRANSFER"` is the
              // post-cancel decision point; `receipt_cancel.post_cancel_action` then
              // says whether to offer Refund or Transfer to deposit. Once a refund is in
              // flight, `payment_refund.next_action` drives the create-instruction step.
              // Each CTA is still gated on the cashier's permission; ids for the
              // instruction step come from payment_refund, falling back to the feed.
              const ap = appr[pId(p)] || {};
              const refundId   = wfRefund?.refund_id || wfRefund?.id || ap.refundId || st.refundId;
              const refundMode = wfRefund?.refund_mode || ap.refundMode || null;
              const ctas = [];
              if (wfRefund && REFUND_INSTRUCTION_ACTIONS.includes(up(wfRefund.next_action)) && can(BILLING_PERMS.PAYMENT_REFUND_EXECUTE)) {
                // Refund requested + approved → record the payout instruction.
                ctas.push({ label: "Refund instruction", onClick: () => setModal({ type: "refundInstruction", payment: p, refundId, refundMode }) });
              } else if (!wfRefund && up(wfCancel.next_action) === CANCEL_DECISION_ACTION) {
                // Post-cancel decision point — offer the disposition chosen at cancel time.
                if (up(wfCancel.post_cancel_action) === POST_CANCEL_TRANSFER && can(BILLING_PERMS.DEPOSIT_TRANSFER_FROM_CANCELLED_RECEIPT)) {
                  ctas.push({ label: "Transfer to deposit", onClick: () => setModal({ type: "transfer", payment: p }) });
                } else if (up(wfCancel.post_cancel_action) === POST_CANCEL_REFUND && can(BILLING_PERMS.PAYMENT_REFUND_REQUEST)) {
                  ctas.push({ label: "Refund", onClick: () => setModal({ type: "refundRequest", payment: p }) });
                }
              }

              return (
                <tr key={pId(p) || pNumber(p)} style={rowStyle}>
                  <td style={numStyle}>{pNumber(p)}</td>
                  <td style={{ padding: "12px", color: C.textSub }}>{pMode(p)}</td>
                  <td style={{ padding: "12px", textAlign: "right", fontWeight: "700", color: C.text }}>{fmtINR(pAmount(p))}</td>
                  <td style={{ padding: "12px 12px 12px 36px" }}>
                    <Badge text={badge.text} tone={badge.tone} />
                  </td>
                  <td style={{ padding: "12px" }}>
                    <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end", alignItems: "center" }}>
                      {ctas.map((cta) => (
                        <IpdButton
                          key={cta.label}
                          size="small"
                          variant={cta.label === "Transfer to deposit" ? "outlined" : "contained"}
                          onClick={cta.disabled ? undefined : cta.onClick}
                          disabled={cta.disabled || busy}
                          startIcon={cta.label === "Transfer to deposit" ? <SvgIcon name="wallet" size={13} color={IPD_COLORS.primary} /> : undefined}
                        >
                          {cta.label}
                        </IpdButton>
                      ))}
                      {showRequestCancel && (
                        <IpdButton
                          size="small"
                          variant="outlined"
                          onClick={() => setModal({ type: "cancel", payment: p })}
                          disabled={busy}
                          sx={{
                            color: IPD_COLORS.error.text,
                            borderColor: IPD_COLORS.error.text,
                            "&:hover": { borderColor: IPD_COLORS.error.text, backgroundColor: IPD_COLORS.error.bg },
                          }}
                        >
                          Request Cancel
                        </IpdButton>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {modal?.type === "cancel" && (
        <RequestCancelModal payment={modal.payment} busy={busy} onClose={() => setModal(null)} onSubmit={doRequestCancel} />
      )}
      {modal?.type === "refundRequest" && (
        <RefundRequestModal payment={modal.payment} busy={busy} onClose={() => setModal(null)} onSubmit={doRefundRequest} />
      )}
      {modal?.type === "refundInstruction" && (
        <RefundInstructionModal payment={modal.payment} presetMode={modal.refundMode} busy={busy} onClose={() => setModal(null)} onSubmit={doRefundInstruction} />
      )}
      {modal?.type === "transfer" && (
        <TransferToDepositModal payment={modal.payment} busy={busy} onClose={() => setModal(null)} onSubmit={doTransfer} />
      )}
    </div>
  );
}
